import { useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useCallback } from "react";
import { useConfirm } from "@/components/providers/confirmProvider";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import { BookingStep } from "@/types/bookingTypes";

export function useConfirmReEnterPhoneNumber() {
  const { getConfirm } = useConfirm();
  const theme = useTheme();

  return useCallback(async () => {
    const ok = await getConfirm({
      title: `Are You Sure you want to go back?`,
      description: `Returning to a previous step will require you to reenter your phone number for verification. Do you want to continue?`,
      discardButtonText: "No, stay here",
      confirmButtonText: "Yes, go back",
      customTheme: theme,
    });
    return ok;
  }, [getConfirm, theme]);
}

/**
 * Hook for handling expired selective booking link redirections
 */
export function useInvalidSelectiveBookingLinkRedirection() {
  const router = useRouter();
  const { getConfirm } = useConfirm();
  const theme = useTheme();

  return useCallback(async () => {
    const confirmed = await getConfirm({
      title: "Invalid Custom Booking Link",
      description:
        "The custom booking link you are using is no longer valid. You will be redirected to the regular booking page.",
      confirmButtonText: "Continue",
      customTheme: theme,
    });

    if (confirmed) {
      router.replace({
        pathname: `/booking/${router.query.slug}`,
      });
      return true;
    }
    return false;
  }, [getConfirm, router, theme]);
}

/**
 * Hook for handling leaving selective booking
 */
export function useLeaveSelectiveBookingLink() {
  const router = useRouter();
  const { getConfirm } = useConfirm();
  const theme = useTheme();

  const handleLeaveSelectiveBookingLink = useCallback(async () => {
    const confirmed = await getConfirm({
      title: "Leave Custom Booking?",
      description:
        "You'll lose access to any special offers or discounts. Do you want to continue?",
      confirmButtonText: "Continue",
      discardButtonText: "Cancel",
      customTheme: theme,
    });

    if (confirmed) {
      /**
       * Remove selectiveBookingLinkId while preserving other query params
       */
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { selectiveBookingLinkId: _, ...restQuery } = router.query;
      await router.replace({ query: restQuery });
    }

    return confirmed;
  }, [getConfirm, router, theme]);

  return handleLeaveSelectiveBookingLink;
}

export function useBookingGoBackButton(activeStep: number, step: BookingStep) {
  const {
    setStep,
    setClientDetails,
    setCode,
    setCard,
    setCodeExpired,
    setIsReturningClient,
  } = useClientBookingContext();

  const getConfirm = useConfirmReEnterPhoneNumber();

  return useCallback(async () => {
    if (activeStep === 1) {
      setStep(BookingStep.SERVICES);
      return;
    }
    if (activeStep === 2) {
      if (
        step === BookingStep.CODE_INPUT ||
        step === BookingStep.PHONE_CONFLICT
      ) {
        setStep(BookingStep.PHONE_NUMBER);
        return;
      }

      setStep(BookingStep.AVAILABILITY_AND_PROVIDER_SELECTION);
      return;
    }
    if (activeStep === 3) {
      const ok = await getConfirm();
      if (!ok) return;

      setStep(BookingStep.PHONE_NUMBER);

      setClientDetails(null);
      setCode(null);
      setCard(null);
      setCodeExpired(false);
      setIsReturningClient(true);
      return;
    }
    return null;
  }, [
    activeStep,
    getConfirm,
    setCard,
    setClientDetails,
    setCode,
    setCodeExpired,
    setIsReturningClient,
    setStep,
    step,
  ]);
}
